import { useState, useEffect } from 'react';
import { supabase, Mockup, Asset, BrandSettings, NotificationPreferences, Template } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// Mockups Hook
export const useMockups = () => {
  const { user } = useAuth();
  const [mockups, setMockups] = useState<Mockup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMockups = async () => {
    if (!user) {
      setMockups([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('mockups')
        .select(`
          *,
          template:templates(*),
          primary_image:assets!mockups_primary_image_id_fkey(*),
          secondary_image:assets!mockups_secondary_image_id_fkey(*)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMockups(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteMockup = async (mockupId: string) => {
    const { error } = await supabase
      .from('mockups')
      .delete()
      .eq('id', mockupId)
      .eq('user_id', user?.id);

    if (error) throw error;
    await fetchMockups();
  };

  useEffect(() => {
    fetchMockups();
    // Re-fetch when session changes indirectly via user changes
    // No interval here; rely on auth context updates
  }, [user]);

  return { mockups, loading, error, refetch: fetchMockups, deleteMockup };
};

// Assets Hook
export const useAssets = () => {
  const { user } = useAuth();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAssets = async () => {
    if (!user) {
      setAssets([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('assets')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssets(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const uploadAsset = async (file: File) => {
    if (!user) throw new Error('User not authenticated');

    // Upload to Supabase Storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;
    
    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('assets')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    // Create database record
    const { data, error } = await supabase
      .from('assets')
      .insert({
        user_id: user.id,
        filename: fileName,
        original_name: file.name,
        file_url: uploadData.path,
        file_type: file.type,
        file_size: file.size
      })
      .single();

    if (error) throw error;
    await fetchAssets();
    return data;
  };

  const deleteAsset = async (assetId: string) => {
    const asset = assets.find(a => a.id === assetId);
    if (!asset) return;

    // Delete from storage
    await supabase.storage
      .from('assets')
      .remove([asset.filename]);

    // Delete from database
    const { error } = await supabase
      .from('assets')
      .delete()
      .eq('id', assetId)
      .eq('user_id', user?.id);

    if (error) throw error;
    await fetchAssets();
  };

  useEffect(() => {
    fetchAssets();
  }, [user]);

  return { assets, loading, error, uploadAsset, deleteAsset, refetch: fetchAssets };
};

// Brand Settings Hook
export const useBrandSettings = () => {
  const { user } = useAuth();
  const [brandSettings, setBrandSettings] = useState<BrandSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBrandSettings = async () => {
    if (!user) {
      setBrandSettings(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('brand_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
      setBrandSettings(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateBrandSettings = async (updates: Partial<BrandSettings>) => {
    if (!user) throw new Error('User not authenticated');

    try {
      if (brandSettings) {
        // Update existing
        const { data, error } = await supabase
          .from('brand_settings')
          .update(updates)
          .eq('user_id', user.id)
          .single();

        if (error) throw error;
        setBrandSettings(data);
      } else {
        // Create new
        const { data, error } = await supabase
          .from('brand_settings')
          .insert({
            user_id: user.id,
            ...updates
          })
          .single();

        if (error) throw error;
        setBrandSettings(data);
      }
    } catch (err: any) {
      throw err;
    }
  };

  useEffect(() => {
    fetchBrandSettings();
  }, [user]);

  return { brandSettings, loading, error, updateBrandSettings };
};

// Notification Preferences Hook
export const useNotificationPreferences = () => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPreferences = async () => {
    if (!user) {
      setPreferences(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error;
      setPreferences(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updatePreferences = async (updates: Partial<NotificationPreferences>) => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('notification_preferences')
      .update(updates)
      .eq('user_id', user.id)
      .single();

    if (error) throw error;
    setPreferences(data);
  };

  useEffect(() => {
    fetchPreferences();
  }, [user]);

  return { preferences, loading, error, updatePreferences };
};

// Templates Hook
export const useTemplates = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .order('usage_count', { ascending: false });

      if (error) throw error;
      setTemplates(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  return { templates, loading, error };
};
